<template>
    <div class="card">
        <div class="card-header">
            <h4 class="card-title">
                {{ titleText }}
            </h4>
        </div>
        <form class="card-body me-3 ms-3" @submit.prevent="handleSubmit">
            <h4 class="mb-3">
                {{ $t('users.shops_msg__shop_manager_info') }}
            </h4>
            <div v-for="(inputGroup, index) in userFormConfig" :key="index">
                <!-- 單一物件的情況 -->
                <div v-if="!Array.isArray(inputGroup)" class="form-group-row">
                    <!-- dual-select 類型使用 DualSelectInput -->
                    <DualSelectInput
                        v-if="inputGroup.type === 'dual-select'"
                        ref="inputRefs"
                        v-bind="inputGroup"
                        v-model="userFormData[inputGroup.field]"
                        @valid="handleValidation"
                        @options-loaded="handleOptionsLoaded"
                        @option-added="
                            handleOptionAdded($event, 'user', inputGroup.field)
                        "
                    />
                    <!-- 其他類型使用 UserFormGroupInput -->
                    <UserFormGroupInput
                        v-else
                        ref="inputRefs"
                        v-bind="inputGroup"
                        v-model="userFormData[inputGroup.field]"
                        :add-option-params="getAddOptionParams(inputGroup)"
                        @valid="handleValidation"
                        @option-added="
                            handleOptionAdded($event, 'user', inputGroup.field)
                        "
                    />
                </div>
                <!-- 多個物件（數組）的情況 -->
                <div v-else class="form-group-row d-flex">
                    <div
                        v-for="(inputConfig, subIndex) in inputGroup"
                        :key="subIndex"
                        class="flex-fill"
                    >
                        <!-- dual-select 類型使用 DualSelectInput -->
                        <DualSelectInput
                            v-if="inputConfig.type === 'dual-select'"
                            ref="inputRefs"
                            v-bind="inputConfig"
                            v-model="userFormData[inputConfig.field]"
                            @valid="handleValidation"
                            @options-loaded="handleOptionsLoaded"
                            @option-added="
                                handleOptionAdded(
                                    $event,
                                    'user',
                                    inputConfig.field
                                )
                            "
                        />
                        <!-- 其他類型使用 UserFormGroupInput -->
                        <UserFormGroupInput
                            v-else
                            ref="inputRefs"
                            v-bind="inputConfig"
                            v-model="userFormData[inputConfig.field]"
                            :add-option-params="getAddOptionParams(inputConfig)"
                            @valid="handleValidation"
                            @option-added="
                                handleOptionAdded(
                                    $event,
                                    'user',
                                    inputConfig.field
                                )
                            "
                        />
                    </div>
                </div>
            </div>
            <hr />
            <h4 class="mb-3">
                {{ $t('users.shops_msg__shop_info') }}
            </h4>
            <div v-for="(inputGroup, index) in formConfig" :key="index">
                <!-- 單一物件的情況 -->
                <div v-if="!Array.isArray(inputGroup)" class="form-group-row">
                    <!-- dual-select 類型使用 DualSelectInput -->
                    <DualSelectInput
                        v-if="inputGroup.type === 'dual-select'"
                        ref="inputRefs"
                        v-bind="inputGroup"
                        :key="`shop-dual-${inputGroup.field}-${stationsRefreshKey}`"
                        v-model="formData[inputGroup.field]"
                        @valid="handleValidation"
                        @options-loaded="handleOptionsLoaded"
                        @option-added="
                            handleOptionAdded($event, 'shop', inputGroup.field)
                        "
                    />
                    <!-- 其他字段使用 UserFormGroupInput -->
                    <UserFormGroupInput
                        v-else
                        ref="inputRefs"
                        v-bind="inputGroup"
                        :key="`shop-input-${inputGroup.field}-${stationsRefreshKey}`"
                        v-model="formData[inputGroup.field]"
                        :add-option-params="getAddOptionParams(inputGroup)"
                        @valid="handleValidation"
                        @option-added="
                            handleOptionAdded($event, 'shop', inputGroup.field)
                        "
                    />
                </div>
                <!-- 多個物件（數組）的情況 -->
                <div v-else class="form-group-row d-flex">
                    <div
                        v-for="(inputConfig, subIndex) in inputGroup"
                        :key="subIndex"
                        class="flex-fill"
                    >
                        <!-- dual-select 類型使用 DualSelectInput -->
                        <DualSelectInput
                            v-if="inputConfig.type === 'dual-select'"
                            ref="inputRefs"
                            v-bind="inputConfig"
                            :key="`shop-dual-${inputConfig.field}-${stationsRefreshKey}`"
                            v-model="formData[inputConfig.field]"
                            @valid="handleValidation"
                            @options-loaded="handleOptionsLoaded"
                            @option-added="
                                handleOptionAdded(
                                    $event,
                                    'shop',
                                    inputConfig.field
                                )
                            "
                        />
                        <!-- 其他類型使用 UserFormGroupInput -->
                        <UserFormGroupInput
                            v-else
                            ref="inputRefs"
                            v-bind="inputConfig"
                            :key="`shop-input-${inputConfig.field}-${stationsRefreshKey}`"
                            v-model="formData[inputConfig.field]"
                            :add-option-params="getAddOptionParams(inputConfig)"
                            @valid="handleValidation"
                            @option-added="
                                handleOptionAdded(
                                    $event,
                                    'shop',
                                    inputConfig.field
                                )
                            "
                        />
                    </div>
                </div>
            </div>

            <div class="d-grid gap-2 d-md-flex justify-content-md-start">
                <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="submitDisabled"
                >
                    {{ $t('users.users_msg__user_form_submit') }}
                </button>
                <button
                    type="reset"
                    class="btn btn-secondary"
                    @click="handleReset"
                >
                    {{ $t('users.users_msg__create_account_reset') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { ref, onMounted, inject, computed, watch } from 'vue';
import { UserFormGroupInput, DualSelectInput } from '@/components';
import { useI18n } from 'vue-i18n';
import {
    fetchCreateShopConfig,
    fetchCreateShopManagerConfig,
    createShop,
    createUser,
    editShop,
} from '@/api/users';
import { transformFormPayload } from '@/utils/formPayload';
import { useStore } from 'vuex';

export default {
    name: 'ShopCreate',
    components: {
        UserFormGroupInput,
        DualSelectInput,
    },
    props: {},
    setup(props, { emit }) {
        const formConfig = ref([]);
        const formData = ref({});
        const submitDisabled = ref(true);
        const { t } = useI18n();
        const store = useStore();
        const Swal = inject('$swal');
        const inputRefs = ref([]);

        const userSelf = computed(() => store.state.auth.userSelf);

        const titleText = computed(() => {
            return t('users.shops_msg__create_a_new_shop');
        });

        const userFormConfig = ref([]); // user 建立表單 config
        const userFormData = ref({}); // user 建立表單資料

        // 用於強制刷新 shop stations 組件的 key
        const stationsRefreshKey = ref(0);

        // 處理 options-loaded 事件
        const handleOptionsLoaded = ({ field, options }) => {
            // 可在此處理選項載入完成後的邏輯
            console.log(`Options loaded for field: ${field}`, options);
        };

        // 處理 option-added 事件，當新增選項時同步刷新另一個 stations 字段
        const handleOptionAdded = (newOption, formType, fieldName) => {
            console.log(
                `Option added in ${formType} form, field: ${fieldName}`,
                newOption
            );

            // 只處理 stations 字段
            if (fieldName !== 'stations') return;

            // 強制刷新 shop 的 stations 組件，讓它重新載入選項
            stationsRefreshKey.value++;
        };

        // 輔助函數：找到特定類型的字段
        const findFieldByType = (configs, fieldName, fieldType = null) => {
            for (const config of configs) {
                if (Array.isArray(config)) {
                    const found = findFieldByType(config, fieldName, fieldType);
                    if (found) return found;
                } else {
                    if (config.field === fieldName) {
                        if (!fieldType || config.type === fieldType) {
                            return config;
                        }
                    }
                }
            }
            return null;
        };

        // 輔助函數：找到 stations 字段
        const findStationField = configs => {
            return findFieldByType(configs, 'stations');
        };

        const initialize = async () => {
            try {
                formConfig.value = await fetchCreateShopConfig();
                userFormConfig.value = await fetchCreateShopManagerConfig();

                // 處理 userFormConfig
                const processUserConfig = config => {
                    if (Array.isArray(config)) {
                        config.forEach(subConfig => {
                            processUserConfig(subConfig);
                        });
                    } else {
                        // 處理 club_name 字段（readonly）
                        if (
                            config.field === 'club_name' &&
                            config.type === 'readonly'
                        ) {
                            // 直接從 store 中獲取 club_name 和 club
                            userFormData.value.club =
                                userSelf.value?.club || '';
                            userFormData.value.club_name =
                                userSelf.value?.club_name || '';
                            config.value = userSelf.value?.club_name || '';
                            // 設定 payload_field，讓 transformFormPayload 知道要使用 userFormData.club 的值
                            config.payload_field = 'club';
                        }
                        // 處理 club 字段（select）- 新增這個條件
                        else if (
                            config.field === 'club' &&
                            config.type === 'select'
                        ) {
                            // 確保 club select 字段有正確的 options_url
                            if (!config.options_url) {
                                config.options_url = '/users/clubs'; // 或者其他正確的 API 端點
                            }
                            userFormData.value.club =
                                userSelf.value?.club || '';
                            config.value =
                                userSelf.value?.club.toString() || '';
                        }
                        // 處理 stations 字段 - 設置空的 options_url
                        else if (config.field === 'stations') {
                            config.options_url =
                                '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                            userFormData.value[config.field] = [];
                        }
                        // 其他字段
                        else if (
                            config.field !== 'club' &&
                            config.field !== 'club_name'
                        ) {
                            userFormData.value[config.field] =
                                config.value || '';
                        }
                    }
                };

                // 初始化 userFormData
                userFormConfig.value.forEach(config => {
                    processUserConfig(config);
                });

                // 初始化 shop formData
                formConfig.value.forEach(config => {
                    if (Array.isArray(config)) {
                        config.forEach(subConfig => {
                            formData.value[subConfig.field] =
                                subConfig.value || '';
                        });
                    } else {
                        formData.value[config.field] = config.value || '';
                    }
                });

                // 如果 user 已經有 club，更新 user stations 的 options_url
                if (userFormData.value.club) {
                    const userStationField = findStationField(
                        userFormConfig.value
                    );
                    if (userStationField) {
                        const newUrl = `/users/stations?search_fields=club__id&search=${userFormData.value.club}&search_mode=exact`;
                        userStationField.options_url = newUrl;
                    }
                }
            } catch (error) {
                console.error('Error fetching form configuration:', error);
            }
        };

        onMounted(async () => {
            await initialize();
        });

        watch(
            () => userSelf.value,
            val => {
                if (val) initialize();
            },
            { immediate: true }
        );

        // 當 user form 中的 club 變更時，修改 user stations 的 options_url
        watch(
            () => userFormData.value.club,
            async clubId => {
                // 找到 userFormConfig 中的 stations 字段並更新 options_url
                const userStationField = findStationField(userFormConfig.value);
                if (
                    userStationField &&
                    (userStationField.type === 'select' ||
                        userStationField.type === 'multi-select' ||
                        userStationField.type === 'dual-select')
                ) {
                    if (clubId && clubId !== 0) {
                        const newUrl = `/users/stations?search_fields=club__id&search=${clubId}&search_mode=exact`;
                        userStationField.options_url = newUrl;
                    } else {
                        userStationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                }

                // 同時更新 shop formConfig 中的 stations 字段（支援所有 select 類型）
                const shopStationField = findStationField(formConfig.value);
                if (
                    shopStationField &&
                    (shopStationField.type === 'select' ||
                        shopStationField.type === 'multi-select' ||
                        shopStationField.type === 'dual-select')
                ) {
                    if (clubId && clubId !== 0) {
                        const newUrl = `/users/stations?search_fields=club__id&search=${clubId}&search_mode=exact`;
                        shopStationField.options_url = newUrl;
                    } else {
                        shopStationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                    // 清空已選取的 stations
                    formData.value.stations = [];
                    // 強制刷新 shop stations 組件
                    stationsRefreshKey.value++;
                }
            },
            { immediate: true }
        );

        // 當 user form 中的 club_name 變更時，修改 user stations 的 options_url
        watch(
            () => userFormData.value.club_name,
            async clubName => {
                // 找到 userFormConfig 中的 stations 字段並更新 options_url
                const userStationField = findStationField(userFormConfig.value);
                if (
                    userStationField &&
                    (userStationField.type === 'select' ||
                        userStationField.type === 'multi-select' ||
                        userStationField.type === 'dual-select')
                ) {
                    if (clubName && clubName.trim() !== '') {
                        const newUrl = `/users/stations?search_fields=club__name&search=${encodeURIComponent(
                            clubName
                        )}`;
                        userStationField.options_url = newUrl;
                    } else {
                        userStationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                }

                // 同時更新 shop formConfig 中的 stations 字段（支援所有 select 類型）
                const shopStationField = findStationField(formConfig.value);
                if (
                    shopStationField &&
                    (shopStationField.type === 'select' ||
                        shopStationField.type === 'multi-select' ||
                        shopStationField.type === 'dual-select')
                ) {
                    if (clubName && clubName.trim() !== '') {
                        const newUrl = `/users/stations?search_fields=club__name&search=${encodeURIComponent(
                            clubName
                        )}`;
                        shopStationField.options_url = newUrl;
                    } else {
                        shopStationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                    // 清空已選取的 stations
                    formData.value.stations = [];
                    // 強制刷新 shop stations 組件
                    stationsRefreshKey.value++;
                }
            },
            { immediate: true }
        );

        const handleValidation = isValid => {
            submitDisabled.value = !isValid;
        };

        const validateAll = () => {
            inputRefs.value.forEach(ref => {
                ref.checkValue();
            });
        };

        const handleSubmit = async () => {
            validateAll();

            if (submitDisabled.value) {
                return;
            }

            Swal.fire({
                title: 'Creating',
                text: 'Please wait...',
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                },
            });

            try {
                // 使用 transformFormPayload 處理 userFormData
                const userData = transformFormPayload(
                    userFormData.value,
                    userFormConfig.value
                );

                // 使用 transformFormPayload 處理 shopData
                const shopData = transformFormPayload(
                    formData.value,
                    formConfig.value
                );
                // 將 club 加入至 shopData 中，指定 club
                shopData.club = userData.club || userFormData.value.club;

                // 1. 建立 shop（不綁定使用者）
                const createdShop = await createShop(shopData);
                const createdShopResData = createdShop.data;
                // 2. 建立 user
                const createdUser = await createUser(userData);
                const createdUserResData = createdUser.data;
                // 3. patch shop manage_user，綁定 createdUser
                await editShop(createdShopResData.id, {
                    manage_user: createdUserResData.id,
                });

                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: t('users.shops_msg__create_a_new_shop_successful', {
                        name: shopData.name,
                    }),
                });
                emit('submitSuccess');
                emit('resetModal'); // 關閉 modal
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: t(
                        `users.${error.response?.data?.detail || error.message}`
                    ),
                });
            }
        };

        const handleReset = async () => {
            // 先清空表單資料，避免 watchers 觸發時使用舊值
            formData.value = {};
            userFormData.value = {};

            // 清空 config，強制重新載入
            formConfig.value = [];
            userFormConfig.value = [];

            // 重新初始化表單，從 API 重新載入設定
            await initialize();
        };

        const getAddOptionParams = inputConfig => {
            // 如果是 stations 欄位，加入 club 參數
            if (inputConfig.field === 'stations') {
                // 獲取 club ID，優先使用 club，如果沒有則嘗試從 userSelf 獲取
                let clubId = userFormData.value.club;

                // 如果 formData.value.club 沒有值，但有 club_name（readonly 模式）
                if (!clubId && userFormData.value.club_name) {
                    clubId = userSelf.value?.club;
                }

                if (!clubId) {
                    console.warn('No club ID available for adding station');
                    return {};
                }

                return {
                    club: clubId,
                };
            }
            // 其他欄位可以根據需要添加不同的參數
            return {};
        };

        return {
            titleText,
            formConfig,
            formData,
            submitDisabled,
            handleSubmit,
            handleValidation,
            handleReset,
            userFormConfig,
            userFormData,
            getAddOptionParams,
            handleOptionsLoaded,
            handleOptionAdded,
            stationsRefreshKey,
        };
    },
};
</script>

<style scoped>
.card {
    box-shadow: none;
}
.form-group-row {
    margin-bottom: 1rem;
    text-align: left;
}

.d-flex {
    display: flex;
    gap: 1rem;
}

.flex-fill {
    flex: 1;
}
</style>
